import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Loader } from "lucide-react";
import { getApiErrorMessage } from "../../../../../utils/utils";
import { isAbortedError } from "../../../../../utils/global";

import { useSnackbar } from "../../../../../_theme/context/SnackbarContext";
import SimpleLoader from "../../../apps/components/SimpleLoader";
import { MyEditor } from "../../../apps/components/MyEditor";
import {
    getAdditionalNotes,
    saveAdditionalNotes,
} from "../core/_request";
import { LogError } from "../../../../../utils/GlobalLogging";

interface AdditionalNotesProps {
    projectId: number;
}

export const AdditionalNotes = ({ projectId }: AdditionalNotesProps) => {
    const { showSnackbar } = useSnackbar();

    const [additionalNotes, setAdditionalNotes] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchAdditionalNotes = useCallback(async () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            setIsLoading(true);

            const response = await getAdditionalNotes(
                projectId,
                controller.signal
            );

            if (controller.signal.aborted) return;

            if (response.status === "ok") {
                setAdditionalNotes(
                    response.data?.eval_additional_notes ?? ""
                );
                return;
            }

            const message =
                response.message || "Failed to fetch Additional Notes";

            showSnackbar(message, "danger");
        } catch (error) {
            if (isAbortedError(error)) return;

            LogError(
                "Error fetching Additional Notes:",
                error,
                "fetchAdditionalNotes"
            );

            showSnackbar(getApiErrorMessage(error, "Failed to fetch Additional Notes"),
                "danger"
            );
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [projectId, showSnackbar]);

    const handleSaveAdditionalNotes = useCallback(async () => {
        try {
            setIsSaving(true);

            const response = await saveAdditionalNotes(
                projectId,
                additionalNotes
            );

            if (response.status === "ok") {
                setAdditionalNotes(
                    response.data?.eval_additional_notes ?? additionalNotes
                );

                showSnackbar(
                    "Additional Notes saved successfully",
                    "success"
                );

                return;
            }

            showSnackbar(
                response.message || "Failed to Save Additional Notes",
                "danger"
            );
        } catch (error) {
            LogError(
                "Error Saving Additional Notes:",
                error,
                "handleSaveAdditionalNotes"
            );

            showSnackbar(getApiErrorMessage(error, "Failed to Save Additional Notes"),
                "danger"
            );
        } finally {
            setIsSaving(false);
        }
    }, [projectId, additionalNotes, showSnackbar]);

    useEffect(() => {
        if (projectId) {
            fetchAdditionalNotes();
        }
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [fetchAdditionalNotes, projectId]);

    return (
        <>
            {isLoading && <SimpleLoader />}

            <div className="card shadow-sm p-4">
                <MyEditor
                    isMiniEditor={false}
                    value={additionalNotes}
                    setValue={setAdditionalNotes}
                    height={300}
                    disabled={isLoading || isSaving}
                />

                <div className="d-flex justify-content-end mt-4">
                    <Button
                        variant="primary"
                        onClick={handleSaveAdditionalNotes}
                        disabled={isLoading || isSaving}
                    >
                        Save Note

                        {isSaving && (
                            <Loader
                                size={16}
                                className="ms-2 animate-spin"
                            />
                        )}
                    </Button>
                </div>
            </div>
        </>
    );
};